import { endOfMonth, format, parseISO } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { MonthRangePicker as MonthRangeCalendar } from '@/components/ui/monthrangepicker'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

const toIso = (d: Date) => format(d, 'yyyy-MM-dd')
const fmt = (d: Date) => format(d, 'MMM yyyy')

interface MonthRangePickerProps {
  /** Inclusive bounds as `yyyy-MM-dd`; `start` snaps to the 1st, `end` to month-end. */
  start: string
  end: string
  onChange: (start: string, end: string) => void
  className?: string
}

/** Generic month-range picker: a popover wrapping the month-range calendar grid. */
export function MonthRangePicker({ start, end, onChange, className }: MonthRangePickerProps) {
  const [open, setOpen] = useState(false)
  const startDate = parseISO(start)
  const endDate = parseISO(end)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn('h-10 w-72 justify-start text-left font-normal', className)}>
          <CalendarIcon className="mr-2 size-4" />
          {`${fmt(startDate)} – ${fmt(endDate)}`}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <MonthRangeCalendar
          selectedMonthRange={{ start: startDate, end: endDate }}
          onMonthRangeSelect={({ start: s, end: e }) => {
            onChange(toIso(s), toIso(endOfMonth(e)))
            setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
